import { useEffect, useState, type FormEvent } from "react";
import { ClipboardList, Plus, ChevronLeft, ChevronRight, Pencil, Trash2 } from "lucide-react";
import { apiFetch } from "../api/client";
import { useWorkspace } from "../contexts/WorkspaceContext";

type TimeEntry = {
  id: string;
  description: string;
  started_at: string;
  ended_at: string;
  duration_seconds: number;
  project_id: string | null;
  source: "manual" | "timer";
};

type Project = { id: string; name: string; color: string };

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function toLocalDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function getWeekRange(offset: number): { from: string; to: string } {
  const now = new Date();
  const dayOfWeek = now.getDay() === 0 ? 7 : now.getDay();
  const monday = new Date(now);
  monday.setDate(now.getDate() - dayOfWeek + 1 + offset * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return {
    from: toLocalDateString(monday),
    to: toLocalDateString(sunday),
  };
}

function toLocalDatetimeInput(iso: string): string {
  const d = new Date(iso);
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
}

export function Timesheets() {
  const { activeWorkspace } = useWorkspace();
  const [weekOffset, setWeekOffset] = useState(0);
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editEntry, setEditEntry] = useState<TimeEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleteError, setDeleteError] = useState("");

  const [desc, setDesc] = useState("");
  const [startedAt, setStartedAt] = useState("");
  const [endedAt, setEndedAt] = useState("");
  const [projectId, setProjectId] = useState("");
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const { from, to } = getWeekRange(weekOffset);

  async function loadEntries() {
    setLoading(true);
    const res = await apiFetch(`/time-entries?from=${from}&to=${to}`);
    if (res.ok) setEntries(await res.json());
    setLoading(false);
  }

  useEffect(() => {
    let cancelled = false;
    apiFetch("/projects").then(async (r) => {
      if (r.ok && !cancelled) setProjects(await r.json());
    });
    return () => { cancelled = true; };
  }, [activeWorkspace]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      const res = await apiFetch(`/time-entries?from=${from}&to=${to}`);
      if (cancelled) return;
      if (res.ok) setEntries(await res.json());
      setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [weekOffset, activeWorkspace]);

  function openCreate() {
    setEditEntry(null);
    const now = new Date();
    const oneHourAgo = new Date(now.getTime() - 3600000);
    setDesc("");
    setStartedAt(toLocalDatetimeInput(oneHourAgo.toISOString()));
    setEndedAt(toLocalDatetimeInput(now.toISOString()));
    setProjectId("");
    setFormError("");
    setShowModal(true);
  }

  function openEdit(entry: TimeEntry) {
    setEditEntry(entry);
    setDesc(entry.description);
    setStartedAt(toLocalDatetimeInput(entry.started_at));
    setEndedAt(toLocalDatetimeInput(entry.ended_at));
    setProjectId(entry.project_id ?? "");
    setFormError("");
    setShowModal(true);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError("");
    if (new Date(endedAt) <= new Date(startedAt)) {
      setFormError("End time must be after start time");
      return;
    }
    setSaving(true);
    const body = {
      description: desc,
      started_at: new Date(startedAt).toISOString(),
      ended_at: new Date(endedAt).toISOString(),
      project_id: projectId || null,
    };
    const res = editEntry
      ? await apiFetch(`/time-entries/${editEntry.id}`, {
          method: "PUT",
          body: JSON.stringify(body),
        })
      : await apiFetch("/time-entries", { method: "POST", body: JSON.stringify(body) });
    if (res.ok) {
      setShowModal(false);
      loadEntries();
    } else {
      const data = await res.json().catch(() => ({}));
      setFormError((data as { detail?: string }).detail ?? "Save failed");
    }
    setSaving(false);
  }

  async function handleDelete(id: string) {
    if (!confirm("Delete this entry?")) return;
    setDeleteError("");
    const res = await apiFetch(`/time-entries/${id}`, { method: "DELETE" });
    if (res.ok || res.status === 204) {
      loadEntries();
    } else {
      setDeleteError("Failed to delete entry. Please try again.");
    }
  }

  const totalSeconds = entries.reduce((sum, e) => sum + e.duration_seconds, 0);

  const inputCls = "w-full bg-field border border-white/[0.08] rounded-lg px-3 py-2 text-ink text-sm focus:outline-none focus:border-brand transition-colors";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-ink flex items-center gap-2">
          <ClipboardList size={22} className="text-brand" />
          Timesheets
        </h1>
        <button
          onClick={openCreate}
          className="bg-brand hover:bg-brand-2 text-white text-sm px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5"
        >
          <Plus size={15} />
          Entry
        </button>
      </div>

      {deleteError && (
        <p className="text-red-400 text-sm bg-red-400/10 rounded-lg px-3 py-2">{deleteError}</p>
      )}

      <div className="flex items-center space-x-3">
        <button
          onClick={() => setWeekOffset((o) => o - 1)}
          className="text-ink-2 hover:text-ink p-1 rounded transition-colors"
        >
          <ChevronLeft size={16} />
        </button>
        <span className="text-ink-2 text-sm font-medium">
          {from} — {to}
        </span>
        <button
          onClick={() => setWeekOffset((o) => o + 1)}
          disabled={weekOffset >= 0}
          className="text-ink-2 hover:text-ink p-1 rounded transition-colors disabled:opacity-30"
        >
          <ChevronRight size={16} />
        </button>
        <span className="text-ink-3 text-sm ml-auto">
          Total: {formatDuration(totalSeconds)}
        </span>
      </div>

      <div className="bg-card rounded-xl divide-y divide-white/[0.06] border border-white/[0.05]">
        {loading ? (
          <p className="p-4 text-ink-2 text-sm">Loading…</p>
        ) : entries.length === 0 ? (
          <p className="p-4 text-ink-3 text-sm">No entries this week.</p>
        ) : (
          entries.map((entry) => (
            <div key={entry.id} className="p-4 flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-ink text-sm font-medium truncate">
                  {entry.description || "No description"}
                </p>
                <p className="text-ink-3 text-xs mt-0.5">
                  {new Date(entry.started_at).toLocaleDateString()} · {entry.source}
                </p>
              </div>
              <div className="flex items-center space-x-4 ml-4 shrink-0">
                <span className="text-ink-2 text-sm">
                  {formatDuration(entry.duration_seconds)}
                </span>
                <button
                  onClick={() => openEdit(entry)}
                  className="text-ink-3 hover:text-ink transition-colors"
                  title="Edit"
                >
                  <Pencil size={14} />
                </button>
                <button
                  onClick={() => handleDelete(entry.id)}
                  className="text-red-400/60 hover:text-red-400 transition-colors"
                  title="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-white/[0.08] rounded-2xl p-6 w-full max-w-md space-y-4 shadow-2xl">
            <h2 className="text-lg font-semibold text-ink">
              {editEntry ? "Edit Entry" : "New Entry"}
            </h2>
            {formError && (
              <p className="text-red-400 text-sm bg-red-400/10 rounded-lg px-3 py-2">{formError}</p>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm text-ink-2 mb-1">Description</label>
                <input
                  type="text"
                  value={desc}
                  onChange={(e) => setDesc(e.target.value)}
                  className={inputCls}
                  placeholder="What did you work on?"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-ink-2 mb-1">Start</label>
                  <input
                    type="datetime-local"
                    required
                    value={startedAt}
                    onChange={(e) => setStartedAt(e.target.value)}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-sm text-ink-2 mb-1">End</label>
                  <input
                    type="datetime-local"
                    required
                    value={endedAt}
                    onChange={(e) => setEndedAt(e.target.value)}
                    className={inputCls}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-ink-2 mb-1">Project</label>
                <select
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                  className="w-full bg-field border border-white/[0.08] rounded-lg pl-3 pr-8 py-2 text-sm text-ink-2 focus:outline-none focus:border-brand transition-colors"
                >
                  <option value="">No project</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm text-ink-3 hover:text-ink transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="bg-brand hover:bg-brand-2 text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50 transition-colors"
                >
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
